import { FaroButton, FaroDialog, FaroFormField, FaroLabel, FaroOverlayModal } from '@faro/design-system';
import { FaroTextInputField } from '@faro/forms';
import DOMPurify from 'dompurify';
import { Form, Formik } from 'formik';
import { ReactNode, Ref } from 'react';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { UserLink } from '../UserLinkNav/UserLinkNav';
import styles from './CreateNewUserLinkModal.module.scss';

// Custom safe check using DOMPurify
const isSafeText = (value: string): boolean => {
    const cleaned = DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    return value === cleaned;
};

const schema = z.object({
    label: z
        .string()
        .min(1, 'Enter a valid label')
        .refine(isSafeText, { message: 'Label contains unsafe characters or HTML' }),

    url: z.string().url('Enter a valid URL'),
});

const validationSchema = toFormikValidationSchema(schema);

export interface CreateNewUserLinkFormProps {
    ref?: Ref<HTMLDivElement>;
    onSave: (values: UserLink) => void;
    onClose: () => void;
}

export function CreateNewUserLinkModal(props: CreateNewUserLinkFormProps): ReactNode {
    const { onSave, onClose, ref, ...rest } = props;

    return (
        <FaroOverlayModal showCloseButton ref={ref} {...rest}>
            <Formik
                enableReinitialize={true}
                initialValues={{
                    id: uuid(),
                    label: '',
                    url: '',
                }}
                onSubmit={values => {
                    onSave({ ...values });
                }}
                validationSchema={validationSchema}
            >
                {({ dirty, isValid }) => {
                    return (
                        <Form>
                            <FaroDialog className={styles.modal}>
                                <FaroDialog.Header className={styles.header}>Lab Links</FaroDialog.Header>
                                <FaroDialog.Body className={styles.body}>
                                    <FaroFormField className={styles.bodyItem}>
                                        <FaroLabel htmlFor="label" className={styles.label}>
                                            Label
                                        </FaroLabel>
                                        <FaroTextInputField name="label" placeholder="Link label" />
                                        <span>Usually the name of the feature you're presenting</span>
                                    </FaroFormField>
                                    <FaroFormField className={styles.bodyItem}>
                                        <FaroLabel htmlFor="url" className={styles.label}>
                                            Target URL
                                        </FaroLabel>
                                        <FaroTextInputField name="url" placeholder="URL" />
                                        <span>Accessible web address for the feature demo</span>
                                    </FaroFormField>
                                </FaroDialog.Body>
                                <FaroDialog.Controls className={styles.controls}>
                                    <FaroButton onClick={onClose} className={styles.controlsButton} appearance="inline">
                                        Cancel
                                    </FaroButton>
                                    <FaroButton
                                        type="submit"
                                        disabled={!dirty || !isValid}
                                        className={`${styles.controlsButton} ${styles.saveButton}`}
                                    >
                                        Save
                                    </FaroButton>
                                </FaroDialog.Controls>
                            </FaroDialog>
                        </Form>
                    );
                }}
            </Formik>
        </FaroOverlayModal>
    );
}

export default CreateNewUserLinkModal;
